import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, CheckSquare, History, RotateCcw, Check, CalendarClock, Paperclip, ClipboardList, Clock, ChevronDown, Pencil, X, UserX, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Taches() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [taskHistory, setTaskHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loading, setLoading] = useState(true);
    const [completingId, setCompletingId] = useState(null);
    const [editingDateId, setEditingDateId] = useState(null);
    const [tempDate, setTempDate] = useState('');
    const [historyFilter, setHistoryFilter] = useState('all'); // today | yesterday | 7d | 30d | all
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const canSeeTasks = ['admin', 'assistante', 'responsable', 'commercial'].includes(user?.role);
    const isAdmin = user?.role === 'admin';

    const fetchTasks = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/tasks?status=active`, { withCredentials: true });
            setTasks(res.data);
        } catch {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/tasks?status=completed`, { withCredentials: true });
            setTaskHistory(res.data);
            setShowHistory(true);
        } catch {
            toast.error('Erreur');
        }
    };

    const handleComplete = async (task) => {
        if (!task?.id) return;
        setCompletingId(task.id);
        try {
            await axios.post(`${API_URL}/api/tasks/${task.id}/complete`, {}, { withCredentials: true });
            toast.success('Tâche terminée');
            fetchTasks();
        } catch {
            toast.error('Erreur');
        } finally {
            setCompletingId(null);
        }
    };

    const confirmDeleteTask = async () => {
        if (!taskToDelete?.id) { setTaskToDelete(null); return; }
        setDeletingId(taskToDelete.id);
        try {
            await axios.delete(`${API_URL}/api/tasks/${taskToDelete.id}`, { withCredentials: true });
            toast.success('Tâche supprimée');
            setTaskToDelete(null);
            if (showHistory) fetchHistory(); else fetchTasks();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setDeletingId(null);
        }
    };

    const saveDueDate = async (taskId, newDate) => {
        try {
            await axios.patch(`${API_URL}/api/tasks/${taskId}`, { due_date: newDate || null }, { withCredentials: true });
            toast.success(newDate ? 'Date mise à jour' : 'Date retirée');
            setEditingDateId(null);
            setTempDate('');
            fetchTasks();
        } catch {
            toast.error('Erreur');
        }
    };

    useEffect(() => { if (canSeeTasks) fetchTasks(); else setLoading(false); }, []);

    const filteredHistory = useMemo(() => {
        if (historyFilter === 'all') return taskHistory;
        const now = new Date();
        const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
        const today = startOfDay(now);
        return taskHistory.filter((t) => {
            if (!t.completed_at) return false;
            const c = new Date(t.completed_at);
            if (historyFilter === 'today') return startOfDay(c).getTime() === today.getTime();
            if (historyFilter === 'yesterday') {
                const y = new Date(today); y.setDate(y.getDate() - 1);
                return startOfDay(c).getTime() === y.getTime();
            }
            if (historyFilter === '7d') {
                const d7 = new Date(today); d7.setDate(d7.getDate() - 7);
                return c >= d7;
            }
            if (historyFilter === '30d') {
                const d30 = new Date(today); d30.setDate(d30.getDate() - 30);
                return c >= d30;
            }
            return true;
        });
    }, [taskHistory, historyFilter]);

    return (
        <Layout>
            <div className="space-y-6" data-testid="taches-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm" data-testid="taches-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Taches a effectuer
                    </h1>
                </div>

                <Card className="shadow-sm" data-testid="taches-card">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                                <CheckSquare className="w-5 h-5 text-[#C9A962]" />
                                {showHistory ? 'Historique des taches' : 'Taches actives'}
                                <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-xs ml-1">
                                    {showHistory ? filteredHistory.length : tasks.length}
                                </Badge>
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                {showHistory && (
                                    <Select value={historyFilter} onValueChange={setHistoryFilter}>
                                        <SelectTrigger className="w-40 h-8 text-xs" data-testid="history-filter">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Tout l'historique</SelectItem>
                                            <SelectItem value="today">Aujourd'hui</SelectItem>
                                            <SelectItem value="yesterday">Hier</SelectItem>
                                            <SelectItem value="7d">7 derniers jours</SelectItem>
                                            <SelectItem value="30d">30 derniers jours</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                {showHistory ? (
                                    <Button variant="ghost" size="sm" className="text-[#1B365D] text-xs" onClick={() => setShowHistory(false)} data-testid="taches-active-btn">
                                        Taches actives
                                    </Button>
                                ) : (
                                    <Button variant="ghost" size="sm" className="text-[#C9A962]" onClick={fetchHistory} data-testid="taches-history-btn">
                                        <History className="w-4 h-4 mr-1" /> Historique
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                            </div>
                        ) : showHistory ? (
                            <div className="space-y-3">
                                {filteredHistory.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <Check className="h-8 w-8 text-gray-300 mb-2" />
                                        <p className="text-gray-400 text-sm">Aucun historique {historyFilter !== 'all' ? 'pour cette période' : ''}</p>
                                    </div>
                                ) : (
                                    filteredHistory.map((t, i) => {
                                        if (t.type === 'manual') {
                                            return (
                                                <div key={t.id || i} className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                                                    <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-green-800 whitespace-pre-wrap break-words">{t.text}</p>
                                                        {t.completed_at && (
                                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                                Terminée le {new Date(t.completed_at).toLocaleDateString('fr-FR')} à {new Date(t.completed_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0">Fait</Badge>
                                                    {isAdmin && t.id && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                                            onClick={() => setTaskToDelete(t)}
                                                            data-testid={`delete-task-${t.id}`}
                                                            title="Supprimer la tâche"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        }
                                        if (t.type === 'cancelled_visit') {
                                            return (
                                                <div key={t.id || i} className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                                                    <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-green-800 truncate">RDV annulé</p>
                                                        <p className="text-xs text-green-700 font-medium">
                                                            {t.event_title}{t.prospect_name ? ` - ${t.prospect_name}` : ''}
                                                        </p>
                                                        {t.cancel_reason && (
                                                            <p className="text-xs text-green-700 mt-0.5 italic">Raison : {t.cancel_reason}</p>
                                                        )}
                                                        {t.completed_at && (
                                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                                Clôturée le {new Date(t.completed_at).toLocaleDateString('fr-FR')} à {new Date(t.completed_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0">Fait</Badge>
                                                    {isAdmin && t.id && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                                            onClick={() => setTaskToDelete(t)}
                                                            data-testid={`delete-task-${t.id}`}
                                                            title="Supprimer la tâche"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return (
                                        <div key={t.id || i} className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                                            <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-green-800 truncate">
                                                    {t.event_title}{t.prospect_name ? ` - ${t.prospect_name}` : ''}
                                                </p>
                                                <p className="text-xs text-green-600">
                                                    Reporte du {t.old_date} ({t.old_start_time}-{t.old_end_time}) {'->'} {t.new_date} ({t.new_start_time}-{t.new_end_time})
                                                </p>
                                                {t.completed_at && (
                                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                                        Reprogramme le {new Date(t.completed_at).toLocaleDateString('fr-FR')} a {new Date(t.completed_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                                                    </p>
                                                )}
                                            </div>
                                            <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0">Fait</Badge>
                                            {isAdmin && t.id && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                                    onClick={() => setTaskToDelete(t)}
                                                    data-testid={`delete-task-${t.id}`}
                                                    title="Supprimer la tâche"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {(!canSeeTasks || tasks.length === 0) ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                            <CheckSquare className="h-8 w-8 text-gray-400" />
                                        </div>
                                        <p className="text-gray-500 font-medium">Aucune tache pour le moment</p>
                                        <p className="text-gray-400 text-sm mt-1">Utilisez "Ajouter une tâche" ou elles apparaitront quand un RDV sera reporté</p>
                                    </div>
                                ) : (
                                    tasks.map((t, i) => {
                                        if (t.type === 'unassigned_event') {
                                            const typeLabel = t.event_type === 'visite_virtuelle'
                                                ? 'Visite virtuelle'
                                                : t.event_type === 'visite_presentiel'
                                                    ? 'Visite présentiel'
                                                    : (t.event_type || 'RDV');
                                            return (
                                                <button
                                                    key={t.id || i}
                                                    type="button"
                                                    onClick={() => navigate(`/agenda/${t.event_id}`)}
                                                    className="w-full text-left flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 hover:border-orange-300 transition-colors"
                                                    data-testid={`tache-${i}`}
                                                >
                                                    <UserX className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-orange-800 truncate">
                                                            RDV non assigné
                                                        </p>
                                                        <p className="text-xs text-orange-700 font-medium">
                                                            {typeLabel}{t.prospect_name ? ` - ${t.prospect_name}` : (t.event_title ? ` - ${t.event_title}` : '')}
                                                        </p>
                                                        <p className="text-xs text-orange-600 mt-0.5">
                                                            Le {t.date} de {t.start_time}{t.end_time ? ` à ${t.end_time}` : ''}
                                                        </p>
                                                        <p className="text-[10px] text-orange-500 italic mt-0.5">
                                                            Cliquez pour assigner un commercial
                                                        </p>
                                                    </div>
                                                    <Badge className="bg-orange-100 text-orange-800 text-[10px] shrink-0">
                                                        À assigner
                                                    </Badge>
                                                </button>
                                            );
                                        }
                                        if (t.type === 'manual') {
                                            const isEditingDate = editingDateId === t.id;
                                            return (
                                                <div key={t.id || i} className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid={`tache-${i}`}>
                                                    <ClipboardList className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                                    <div className="flex-1 min-w-0 space-y-2">
                                                        <p className="text-sm font-medium text-blue-900 whitespace-pre-wrap break-words">{t.text}</p>
                                                        <div className="flex items-center gap-3 flex-wrap text-xs text-blue-700">
                                                            {isEditingDate ? (
                                                                <div className="flex items-center gap-1">
                                                                    <CalendarClock className="w-3.5 h-3.5" />
                                                                    <Input
                                                                        type="date"
                                                                        value={tempDate}
                                                                        onChange={(e) => setTempDate(e.target.value)}
                                                                        className="h-7 text-xs py-0 w-40"
                                                                        autoFocus
                                                                        data-testid={`edit-date-input-${t.id}`}
                                                                    />
                                                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600" onClick={() => saveDueDate(t.id, tempDate)} data-testid={`save-date-${t.id}`}>
                                                                        <Check className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-500" onClick={() => { setEditingDateId(null); setTempDate(''); }} data-testid={`cancel-date-${t.id}`}>
                                                                        <X className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setEditingDateId(t.id); setTempDate(t.due_date || ''); }}
                                                                    className="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-blue-100 border border-transparent hover:border-blue-200 transition-colors"
                                                                    data-testid={`edit-date-btn-${t.id}`}
                                                                >
                                                                    <CalendarClock className="w-3.5 h-3.5" />
                                                                    {t.due_date
                                                                        ? new Date(t.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                                                                        : <span className="italic text-blue-500">Ajouter une date</span>}
                                                                    <Pencil className="w-3 h-3 ml-0.5 opacity-60" />
                                                                </button>
                                                            )}
                                                            {t.file_url && (
                                                                <a href={`${API_URL}${t.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#C9A962] hover:underline">
                                                                    <Paperclip className="w-3.5 h-3.5" />
                                                                    {t.file_name || 'Fichier joint'}
                                                                </a>
                                                            )}
                                                        </div>
                                                        {t.created_at && (
                                                            <p className="text-[10px] text-gray-400">
                                                                Créée le {new Date(t.created_at).toLocaleDateString('fr-FR')} à {new Date(t.created_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                                                                {t.created_by_name ? ` par ${t.created_by_name}` : ''}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="shrink-0 flex flex-col items-end gap-1">
                                                        <Select
                                                            value="en_attente"
                                                            onValueChange={(v) => { if (v === 'fait') handleComplete(t); }}
                                                            disabled={completingId === t.id}
                                                        >
                                                            <SelectTrigger
                                                                className="h-7 text-xs px-2 gap-1 border-amber-300 bg-amber-100 text-amber-800 font-semibold"
                                                                data-testid={`status-select-${t.id}`}
                                                            >
                                                                <Clock className="w-3 h-3" />
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="en_attente">
                                                                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-amber-600" /> En attente</span>
                                                                </SelectItem>
                                                                <SelectItem value="fait">
                                                                    <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-600" /> Fait</span>
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        {isAdmin && t.id && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                onClick={() => setTaskToDelete(t)}
                                                                data-testid={`delete-task-${t.id}`}
                                                                title="Supprimer la tâche"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={t.id || i} className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid={`tache-${i}`}>
                                                {t.type === 'cancelled_visit' ? (
                                                    <X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                                                ) : (
                                                    <RotateCcw className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-semibold truncate ${t.type === 'cancelled_visit' ? 'text-red-800' : 'text-amber-800'}`}>
                                                        {t.type === 'cancelled_visit' ? 'RDV annulé' : 'RDV a reprogrammer'}
                                                    </p>
                                                    <p className={`text-xs font-medium ${t.type === 'cancelled_visit' ? 'text-red-700' : 'text-amber-700'}`}>
                                                        {t.event_title}{t.prospect_name ? ` - ${t.prospect_name}` : ''}
                                                    </p>
                                                    <p className={`text-xs ${t.type === 'cancelled_visit' ? 'text-red-600' : 'text-amber-600'}`}>
                                                        Etait le {t.old_date} ({t.old_start_time}-{t.old_end_time})
                                                    </p>
                                                    {t.type === 'cancelled_visit' && t.cancel_reason && (
                                                        <p className="text-xs text-red-700 mt-0.5 italic">Raison : {t.cancel_reason}</p>
                                                    )}
                                                    {t.created_at && (
                                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                                            {t.type === 'cancelled_visit' ? 'Annulé' : 'Reporte'} le {new Date(t.created_at).toLocaleDateString('fr-FR')} a {new Date(t.created_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="shrink-0 flex flex-col items-end gap-1">
                                                    <Badge className={`text-[10px] shrink-0 ${t.type === 'cancelled_visit' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                                                        En attente
                                                    </Badge>
                                                    {t.type === 'cancelled_visit' && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 px-2 text-xs text-green-700 hover:bg-green-100"
                                                            disabled={completingId === t.id}
                                                            onClick={() => handleComplete(t)}
                                                            data-testid={`complete-cancelled-${t.id}`}
                                                        >
                                                            <Check className="w-3.5 h-3.5 mr-1" /> Clôturer
                                                        </Button>
                                                    )}
                                                    {isAdmin && t.id && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            onClick={() => setTaskToDelete(t)}
                                                            data-testid={`delete-task-${t.id}`}
                                                            title="Supprimer la tâche"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <AlertDialog open={!!taskToDelete} onOpenChange={(open) => { if (!open) setTaskToDelete(null); }}>
                <AlertDialogContent data-testid="delete-task-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer la tâche ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. La tâche sera définitivement supprimée.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-task-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteTask}
                            disabled={!!deletingId}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="delete-task-confirm"
                        >
                            {deletingId ? 'Suppression...' : 'Supprimer'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
